import { Injectable, inject, OnDestroy } from '@angular/core';
import { TokenManagerService } from './token-manager.service';
import { Subject, throttleTime, fromEvent, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SessionActivityService implements OnDestroy {
  private tokenManager = inject(TokenManagerService);
  private destroy$ = new Subject<void>();
  
  // Configuration
  private readonly ACTIVITY_THROTTLE_MS = 30000; // Check activity every 30 seconds
  private readonly SESSION_WARNING_MINUTES = 5; // Warn user 5 minutes before expiry
  private readonly EXTEND_SESSION_ON_ACTIVITY = true;
  
  private lastActivityTime = Date.now();
  private isListening = false;

  constructor() {
    this.startActivityTracking();
  }

  ngOnDestroy(): void {
    this.stopActivityTracking();
  }

  /**
   * Start tracking user activity
   */
  private startActivityTracking(): void {
    if (this.isListening) return;

    const activityEvents = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 
      'touchstart', 'click', 'focus', 'blur'
    ];

    // Merge all activity events
    const activity$ = merge(
      ...activityEvents.map(event => fromEvent(document, event))
    );

    // Throttle activity detection
    activity$.pipe(
      throttleTime(this.ACTIVITY_THROTTLE_MS),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.handleUserActivity();
    });

    this.isListening = true;
  }

  /**
   * Stop tracking user activity
   */
  private stopActivityTracking(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.isListening = false;
  }

  /**
   * Handle user activity detected
   */
  private handleUserActivity(): void {
    this.lastActivityTime = Date.now();
    
    if (this.EXTEND_SESSION_ON_ACTIVITY) {
      // Check if we should extend the session
      const status = this.tokenManager.getRefreshStatus();
      
      if (status.tokenInfo && !status.tokenInfo.error && typeof status.tokenInfo.minutesUntilExpiry === 'number') {
        const minutesUntilExpiry = status.tokenInfo.minutesUntilExpiry;
        
        // If token expires within warning time, extend session
        if (minutesUntilExpiry <= this.SESSION_WARNING_MINUTES) {
          this.tokenManager.extendSession().subscribe({
            next: (extended) => {
              if (extended) {
              }
            },
            error: (error) => {
              console.error('❌ Failed to extend session:', error);
            }
          });
        }
      }
    }
  }

  /**
   * Get user activity information
   */
  getActivityInfo() {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;
    const minutesSinceActivity = Math.floor(timeSinceLastActivity / 1000 / 60);

    return {
      lastActivityTime: new Date(this.lastActivityTime).toISOString(),
      minutesSinceLastActivity: minutesSinceActivity,
      isRecentlyActive: minutesSinceActivity < 5,
      isTracking: this.isListening,
      configuration: {
        activityThrottleMs: this.ACTIVITY_THROTTLE_MS,
        sessionWarningMinutes: this.SESSION_WARNING_MINUTES,
        extendSessionOnActivity: this.EXTEND_SESSION_ON_ACTIVITY
      }
    };
  }

  /**
   * Manually trigger session extension
   */
  extendSessionManually() {
    return this.tokenManager.forceRefresh();
  }

  /**
   * Check if user should be warned about session expiry
   */
  shouldWarnUser(): boolean {
    const status = this.tokenManager.getRefreshStatus();
    
    if (!status.tokenInfo || status.tokenInfo.error) {
      return false;
    }

    const minutesUntilExpiry = status.tokenInfo.minutesUntilExpiry;
    const timeSinceActivity = (Date.now() - this.lastActivityTime) / 1000 / 60;

    // Warn if token expires soon and user has been inactive
    return typeof minutesUntilExpiry === 'number' && 
           minutesUntilExpiry <= this.SESSION_WARNING_MINUTES && 
           timeSinceActivity > 2; // User inactive for more than 2 minutes
  }

  /**
   * Get session warning information
   */
  getSessionWarning() {
    const status = this.tokenManager.getRefreshStatus();
    
    if (!status.tokenInfo || status.tokenInfo.error) {
      return null;
    }

    const minutesUntilExpiry = status.tokenInfo.minutesUntilExpiry;
    
    if (this.shouldWarnUser() && typeof minutesUntilExpiry === 'number') {
      return {
        minutesUntilExpiry,
        message: `Your session will expire in ${minutesUntilExpiry} minutes. Click here to extend it.`,
        action: 'extend_session'
      };
    }

    return null;
  }
} 